import type { JSX } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { layoutNavigation, routePaths, wideLayoutQuery } from "../../app/navigation";
import { useVisibleNavigation } from "../../app/useVisibleNavigation";
import { useI18n } from "../../shared/i18n/I18nContext";
import {
  accountSectionId,
  pendingSignOutRequest,
  useGuardedSignOut,
} from "../../shared/session/signOutRequest";
import type { SignOutRequestState } from "../../shared/session/signOutRequest";
import { useNavigationRoles } from "../../shared/session/useNavigationRoles";
import { Icon } from "../../shared/ui/Icon";
import { PageHeader } from "../../shared/ui/PageHeader";
import styles from "../../shared/ui/ui.module.css";
import { useMediaQuery } from "../../shared/ui/useMediaQuery";
import moreStyles from "./MorePage.module.css";
import { SettingsSections } from "./SettingsSections";

/**
 * "Mehr" on phones: the areas that do not fit into the navigation bar (Material 3 navigation bar,
 * HIG "More" tab), the preferences with the notifications of this device (UX-024) and the account
 * with sign-out; project responsibles find „Projekte“ among the areas (T-125). `/more#notifications`
 * focuses the notifications.
 *
 * Wide screens have no „Mehr“ (F-74): every area is in the navigation drawer and the settings are
 * reached through the account menu, so the address (older links of „Datenschutz“, bookmarks) leads
 * to „Einstellungen“ there, keeping the fragment. A confirmation or error of the sign-out shown when
 * the window becomes wide goes along (F-84): „Einstellungen“ counts the unsent entries again and asks
 * there, so a resized window never drops the question.
 *
 * @returns The page element.
 */
export function MorePage(): JSX.Element {
  const { t } = useI18n();
  const location = useLocation();
  const wide = useMediaQuery(wideLayoutQuery);
  const roles = useNavigationRoles() ?? [];
  const visible = useVisibleNavigation(roles);
  const { overflow } = layoutNavigation(visible);
  const signOut = useGuardedSignOut(null);

  if (wide) {
    const request = pendingSignOutRequest(signOut);
    const state: SignOutRequestState | null = request === null ? null : { signOut: request };
    const hash = request === null ? location.hash : `#${accountSectionId}`;
    const redirect = (
      <Navigate to={{ pathname: routePaths.settings, hash }} state={state} replace />
    );

    return redirect;
  }

  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("nav.more")} />

      {overflow.length === 0 ? null : (
        <nav className={moreStyles["areas"]} aria-label={t("more.areas")}>
          <ul className={moreStyles["list"]}>
            {overflow.map((item) => (
              <li key={item.path}>
                <Link className={moreStyles["entry"]} to={item.path}>
                  <Icon name={item.icon} className={moreStyles["entryIcon"]} />
                  <span className={moreStyles["entryLabel"]}>{t(item.labelKey)}</span>
                  <Icon name="chevronRight" className={moreStyles["chevron"]} />
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <SettingsSections preferencesTitleKey="more.settings" signOut={signOut} />
    </div>
  );

  return page;
}
